import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import SimpleRichEditor from '@/components/SimpleRichEditor';
import { ArrowLeft, Save, Eye, Image, X, Send, Clock } from 'lucide-react';

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, isEditor } = useAuth();
  const isNew = !id;

  const [form, setForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category_id: '',
    tags: [],
    language: 'pt',
    is_featured: false,
    scheduled_at: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data || []));
    if (!isNew) {
      api.get(`/articles/${id}`).then(r => {
        setForm({
          title: r.data.title || '',
          content: r.data.content || '',
          excerpt: r.data.excerpt || '',
          featured_image: r.data.featured_image || '',
          category_id: r.data.category_id || '',
          tags: r.data.tags || [],
          language: r.data.language || 'pt',
          is_featured: r.data.is_featured || false,
          scheduled_at: r.data.scheduled_at || '',
        });
      }).catch(() => {
        toast.error('Artigo não encontrado');
        navigate('/admin/artigos');
      }).finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (!form.tags.includes(tag)) {
        setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleChange('featured_image', `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      toast.success('Imagem carregada');
    } catch (err) {
      toast.error('Erro ao carregar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (status = null) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post('/articles', form);
        toast.success('Artigo criado');
        if (status) {
          await api.patch(`/articles/${res.data.id}/status`, { status });
        }
        navigate('/admin/artigos');
      } else {
        await api.put(`/articles/${id}`, form);
        if (status) {
          await api.patch(`/articles/${id}/status`, { status });
        }
        toast.success('Artigo atualizado');
        navigate('/admin/artigos');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar artigo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="article-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/artigos')} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            {isNew ? t('backoffice.new_article') : 'Editar Artigo'}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <a href={`/noticias/${id}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
              <Eye size={14} /> Pré-visualizar
            </a>
          )}
          <button onClick={() => handleSubmit()} disabled={saving} data-testid="save-draft"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            <Save size={14} /> {saving ? 'A guardar...' : 'Guardar Rascunho'}
          </button>
          <button onClick={() => handleSubmit('pending')} disabled={saving} data-testid="submit-review"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#CC0000] rounded-md hover:bg-[#A30000] disabled:opacity-50">
            <Send size={14} /> Submeter para Revisão
          </button>
          {isEditor() && (
            <button onClick={() => handleSubmit('published')} disabled={saving} data-testid="publish"
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
              Publicar Agora
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Title */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Escreva um título atrativo..."
              data-testid="article-title"
              className="w-full px-4 py-3 text-lg border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            />
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Conteúdo</label>
            <SimpleRichEditor
              value={form.content}
              onChange={(val) => handleChange('content', val)}
              placeholder="Escreva o conteúdo do artigo..."
            />
          </div>

          {/* Excerpt */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Resumo</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Breve descrição do artigo (usado em listagens e SEO)..."
              rows={3}
              data-testid="article-excerpt"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Featured Image */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Imagem de Destaque</label>
            {form.featured_image ? (
              <div className="relative group">
                <img src={form.featured_image} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                <button onClick={() => handleChange('featured_image', '')}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-red-300 hover:bg-red-50/50 transition-colors">
                <Image size={24} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">{uploading ? 'A carregar...' : 'Clique para carregar'}</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
              </label>
            )}
            <input
              type="url"
              value={form.featured_image}
              onChange={(e) => handleChange('featured_image', e.target.value)}
              placeholder="Ou cole o URL da imagem..."
              className="mt-3 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            />
          </div>

          {/* Category */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Categoria</label>
            <select
              value={form.category_id}
              onChange={(e) => handleChange('category_id', e.target.value)}
              data-testid="article-category"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            >
              <option value="">Selecionar categoria...</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  #{tag}
                  <button onClick={() => handleRemoveTag(tag)} className="text-gray-400 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="Escreva e pressione Enter..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            />
          </div>

          {/* Language */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Idioma</label>
            <select
              value={form.language}
              onChange={(e) => handleChange('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            >
              <option value="pt">Português</option>
              <option value="es">Espanhol</option>
              <option value="en">Inglês</option>
            </select>
          </div>

          {/* Options */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => handleChange('is_featured', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700">Artigo em destaque</span>
            </label>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Clock size={14} /> Agendar publicação
              </label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => handleChange('scheduled_at', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
